import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Circle, CheckCircle2 } from 'lucide-react';

import { getPolicyQuiz, savePolicyQuiz } from '../../../services/policyService';
import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput';

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const QuestionCard = styled(Card)`
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
`;

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const RadioButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ $selected }) => $selected ? '#16a34a' : '#D0D5DD'};
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: ${({ $danger }) => $danger ? '#ef4444' : '#64748b'};
  &:hover { background: ${({ $danger }) => $danger ? '#fef2f2' : '#f1f5f9'}; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: ${({ $gap }) => $gap || '12px'};
  align-items: ${({ $align }) => $align || 'flex-start'};
  margin-bottom: ${({ $mb }) => $mb || '0'};
`;

const QuestionCount = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-top: 8px;
`;

export default function QuizTab({ documentId }) {
  const { t } = useTranslation();
  const [passingScore, setPassingScore] = useState('60');
  const [maxAttempts, setMaxAttempts] = useState('3');
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getPolicyQuiz(documentId)
      .then((res) => {
        const quiz = res.data?.data;
        if (quiz) {
          setPassingScore(String(quiz.passingScore ?? 60));
          setMaxAttempts(String(quiz.maxAttempts ?? 3));
          if (quiz.questions && Array.isArray(quiz.questions)) {
            setQuestions(quiz.questions.map((q, i) => ({
              id: q.id || Date.now() + i,
              text: q.text || q.questionText || '',
              options: (q.options || []).map((o, oi) => ({
                id: o.id || Date.now() + i * 100 + oi,
                text: o.text || o.optionText || o.label || '',
                isCorrect: o.isCorrect || false,
              })),
            })));
          }
        }
      })
      .finally(() => setLoaded(true));
  }, [documentId]);

  const addQuestion = useCallback(() => {
    setQuestions((prev) => [...prev, {
      id: Date.now(),
      text: '',
      options: [
        { id: Date.now() + 1, text: '', isCorrect: false },
        { id: Date.now() + 2, text: '', isCorrect: false },
      ],
    }]);
  }, []);

  const removeQuestion = useCallback((qId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== qId));
  }, []);

  const updateQuestionText = useCallback((qId, value) => {
    setQuestions((prev) => prev.map((q) => q.id === qId ? { ...q, text: value } : q));
  }, []);

  const addOption = useCallback((qId) => {
    setQuestions((prev) => prev.map((q) => {
      if (q.id !== qId) return q;
      return { ...q, options: [...q.options, { id: Date.now(), text: '', isCorrect: false }] };
    }));
  }, []);

  const updateOptionText = useCallback((qId, optId, value) => {
    setQuestions((prev) => prev.map((q) => {
      if (q.id !== qId) return q;
      return { ...q, options: q.options.map((o) => o.id === optId ? { ...o, text: value } : o) };
    }));
  }, []);

  // Radio logic: only one correct answer per question
  const setCorrectOption = useCallback((qId, optId) => {
    setQuestions((prev) => prev.map((q) => {
      if (q.id !== qId) return q;
      return {
        ...q,
        options: q.options.map((o) => ({ ...o, isCorrect: o.id === optId })),
      };
    }));
  }, []);

  const removeOption = useCallback((qId, optId) => {
    setQuestions((prev) => prev.map((q) => {
      if (q.id !== qId) return q;
      return { ...q, options: q.options.filter((o) => o.id !== optId) };
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await savePolicyQuiz(documentId, {
        passingScore: Number(passingScore) || 60,
        maxAttempts: Number(maxAttempts) || 3,
        questionCount: questions.length,
        shuffleQuestions: true,
        questions: questions.map((q) => ({
          questionText: q.text,
          questionType: 1, // Always multiple choice
          points: 1,
          options: q.options.map((o) => ({
            optionText: o.text,
            isCorrect: o.isCorrect,
          })),
        })),
      });
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [documentId, passingScore, maxAttempts, questions]);

  if (!loaded) return null;

  return (
    <div>
      <Card>
        <Row $gap="16px" $mb="0">
          <div style={{ width: 180 }}>
            <TextInput
              label={t('policy.passingScore')}
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              type="number"
            />
          </div>
          <div style={{ width: 180 }}>
            <TextInput
              label={t('policy.maxAttempts')}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(e.target.value)}
              type="number"
            />
          </div>
        </Row>
        <QuestionCount>
          {t('policy.questionCountInfo', { count: questions.length })}
        </QuestionCount>
      </Card>

      {questions.map((q, idx) => (
        <QuestionCard key={q.id}>
          <Row $align="center" $mb="12px">
            <span style={{ fontFamily: "'Nunito Sans', sans-serif", fontWeight: 700, fontSize: 14, color: '#004242' }}>
              {t('policy.questionNumber', { number: idx + 1 })}
            </span>
            <div style={{ flex: 1 }} />
            <IconButton $danger onClick={() => removeQuestion(q.id)}>
              <Trash2 size={16} />
            </IconButton>
          </Row>

          <div style={{ marginBottom: 12 }}>
            <TextInput
              label={t('policy.questionText')}
              value={q.text}
              onChange={(e) => updateQuestionText(q.id, e.target.value)}
            />
          </div>

          <div>
            <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 12, fontWeight: 600, color: '#A0A8B4', marginBottom: 8 }}>
              {t('policy.options')} — {t('policy.selectCorrectAnswer')}
            </div>
            {q.options.map((opt) => (
              <OptionRow key={opt.id}>
                <RadioButton $selected={opt.isCorrect} onClick={() => setCorrectOption(q.id, opt.id)}>
                  {opt.isCorrect ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                </RadioButton>
                <div style={{ flex: 1 }}>
                  <TextInput
                    value={opt.text}
                    onChange={(e) => updateOptionText(q.id, opt.id, e.target.value)}
                    placeholder={t('policy.optionPlaceholder')}
                  />
                </div>
                {q.options.length > 2 && (
                  <IconButton $danger onClick={() => removeOption(q.id, opt.id)}>
                    <Trash2 size={14} />
                  </IconButton>
                )}
              </OptionRow>
            ))}
            <Button variant="ghost" onClick={() => addOption(q.id)} style={{ fontSize: 13, padding: '6px 12px' }}>
              <Plus size={14} /> {t('policy.addOption')}
            </Button>
          </div>
        </QuestionCard>
      ))}

      <Card>
        <Row $align="center">
          <Button variant="outline" onClick={addQuestion}>
            <Plus size={16} /> {t('policy.addQuestion')}
          </Button>
        </Row>
      </Card>

      {questions.length > 0 && (
        <Footer>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? '...' : t('common.save')}
          </Button>
        </Footer>
      )}
    </div>
  );
}
